import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import {
  VOLUME_RAMP,
  CONSISTENCY_RAMP,
  STATUS_RAMP,
  DURATION_RAMP,
  toolColor
} from './selfDevelopmentTheme';

/**
 * One tool's page inside Self Development — Gratitude, Cleanse, Journal, Goals
 * or Affirmations. (Vision Board keeps its own page; it is a two-collection
 * feature with an abandonment signal none of these five have.)
 *
 * The top of every page is identical on purpose — take-up, volume, how many
 * distinct days people show up, when in the week they do it — so an admin
 * comparing two tools is comparing like with like, and only the bottom of the
 * page changes.
 *
 * The bottom is where a tool earns its own page:
 *
 *   Gratitude     saved, photographed, described — and the catalogue side
 *   Cleanse       the funnel through the thought record, ending at the reframe
 *   Journal       what somebody does with an entry once it exists
 *   Goals         set → in progress → achieved, plus the ones running late
 *   Affirmations  written their own versus played Shoorah's
 *
 * Every figure on this page is a count of an ACTION. Not one of them is the
 * thing that was written, and none of them is the stored sentiment read on it.
 */

/** Per-tool copy. The noun matters: "entries" is wrong for goals and boards. */
const TOOL_CONFIG = {
  gratitude: {
    key: 'gratitude',
    label: 'Gratitude',
    noun: 'entries',
    peopleTitle: 'People Practising',
    volumeTitle: 'Entries per Person',
    emptyMessage: 'Nobody has written a gratitude entry yet.'
  },
  cleanse: {
    key: 'cleanse',
    label: 'Cleanse',
    noun: 'thought records',
    peopleTitle: 'People Using Cleanse',
    volumeTitle: 'Records per Person',
    emptyMessage: 'Nobody has started a thought record yet.'
  },
  journal: {
    key: 'journal',
    label: 'Journal',
    noun: 'entries',
    peopleTitle: 'People Journalling',
    volumeTitle: 'Entries per Person',
    emptyMessage: 'Nobody has written a journal entry yet.'
  },
  goals: {
    key: 'goals',
    label: 'Goals',
    noun: 'goals',
    peopleTitle: 'People Setting Goals',
    volumeTitle: 'Goals per Person',
    emptyMessage: 'Nobody has set a goal yet.'
  },
  affirmations: {
    key: 'affirmations',
    label: 'Affirmations',
    noun: 'affirmations',
    peopleTitle: 'People Writing Their Own',
    volumeTitle: 'Affirmations per Person',
    emptyMessage: 'Nobody has written their own affirmation yet.'
  }
};

const SelfDevelopmentToolPage = ({ tool, fetcher, populationLabel = 'users' }) => {
  const config = TOOL_CONFIG[tool];

  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (!config) return <PanelEmpty>Unknown tool.</PanelEmpty>;

  const accent = toolColor(tool);
  const entries = stats?.entries || 0;
  const volumeSplit = stats?.volumeSplit || [];
  const consistencySplit = stats?.consistencySplit || [];
  const weekday = stats?.weekday || [];
  const detail = stats?.detail || [];
  const entriesTrend = stats?.entriesTrend || [];
  const peopleTrend = stats?.peopleTrend || [];

  const trendHasData = entriesTrend.some((value) => value > 0);

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line', stacked: false },
    colors: [accent, CHART_COLORS.moss],
    stroke: { width: [0, 3], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(entriesTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [accent],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekday.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} ${config.noun}` } }
  };

  /** A `[{label, count, share}]` list rendered as a table of counts and shares. */
  const shareTable = (rows, firstHeading) => (
    <PanelTable
      head={
        <>
          <th className="py-2 pr-4 font-medium">{firstHeading}</th>
          <th className="py-2 px-4 font-medium text-center">Count</th>
          <th className="py-2 font-medium text-right">Share</th>
        </>
      }
    >
      {rows.map((row) => (
        <tr key={row.label} className={PANEL_ROW_CLASS}>
          <td className="py-2 pr-4">{row.label}</td>
          <td className="py-2 px-4 text-center">{formatBigNumber(row.count)}</td>
          <td className="py-2 text-right">{row.share}%</td>
        </tr>
      ))}
    </PanelTable>
  );

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline: identical on all five pages ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title={config.peopleTitle}
          value={stats ? formatBigNumber(stats.users) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={stats?.totalUsers > 0 ? `${stats.adoption}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title={`Total ${config.noun}`}
          value={stats ? formatBigNumber(entries) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.custom}
          title="Per Person"
          value={stats ? stats.avgPerUser : '-'}
        />
      </div>

      {/* ---------- Volume, consistency, weekday ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title={config.volumeTitle}
          subtitle="How much one person writes"
          data={volumeSplit}
          colors={VOLUME_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={5}
        />
        <MiniDonutCard
          title="Days Practised"
          subtitle="Distinct days, not entries"
          data={consistencySplit}
          colors={CONSISTENCY_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={5}
        />
        <Panel title="When in the Week" subtitle={`${config.label} activity by weekday`}>
          {entries > 0 ? (
            <Chart
              type="bar"
              height={260}
              width="100%"
              series={[{ name: config.label, data: weekday.map((day) => day.count) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>{config.emptyMessage}</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Trend ---------- */}
      <div className="mt-6">
        <Panel
          title={`${config.label} Over Time`}
          subtitle={`${config.noun[0].toUpperCase()}${config.noun.slice(
            1
          )} and people — last 30 days`}
        >
          {trendHasData ? (
            <Chart
              type="line"
              height={340}
              width="100%"
              series={[
                { name: config.label, type: 'column', data: entriesTrend },
                { name: 'People', type: 'line', data: peopleTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>Nothing written in the last 30 days.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ================= Tool-specific ================= */}

      {(tool === 'gratitude' || tool === 'journal' || tool === 'affirmations') && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
          <Panel
            title="What People Do With an Entry"
            subtitle="Actions taken, never the entry itself"
          >
            {/* A table, not bars: these rows overlap — one entry can be saved
                AND have a photo AND be tagged — so each share is of all
                entries, and bars scaled against their own sum would invent a
                distribution that doesn't exist. */}
            {entries > 0 ? (
              shareTable(detail, 'Action')
            ) : (
              <PanelEmpty>{config.emptyMessage}</PanelEmpty>
            )}
          </Panel>

          {tool === 'journal' && (
            <Panel title="How People Organise" subtitle="Folders in use across your people">
              {entries > 0 ? (
                <>
                  <p className="text-[56px] leading-none font-semibold text-shoorah-btn dark:text-white tracking-tight">
                    {formatBigNumber(stats.foldersInUse || 0)}
                  </p>
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    Folders holding at least one entry. Folder names are not read — only how many
                    exist, because somebody who files their journal is somebody who intends to come
                    back to it.
                  </p>
                </>
              ) : (
                <PanelEmpty>{config.emptyMessage}</PanelEmpty>
              )}
            </Panel>
          )}

          {tool === 'gratitude' && (
            <Panel
              title="From Shoorah's Catalogue"
              subtitle="Published gratitudes your people saved"
            >
              <p className="text-[56px] leading-none font-semibold text-shoorah-btn dark:text-white tracking-tight">
                {formatBigNumber(stats?.catalogueSaved || 0)}
              </p>
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                Saves of Shoorah's own gratitude content — the other half of the feature, and the
                lower-effort way in. People who save these and never write their own are the group a
                gratitude campaign can move.
              </p>
            </Panel>
          )}

          {tool === 'affirmations' && (
            <Panel title="Written Their Own or Played Shoorah's" subtitle="Two habits, kept apart">
              {/* Deliberately not added together: writing your own affirmation
                  and pressing play on one are not the same act, and a combined
                  total would hide which of the two a company actually has. */}
              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Measure</th>
                    <th className="py-2 font-medium text-right">Count</th>
                  </>
                }
              >
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Wrote their own</td>
                  <td className="py-2 text-right">{formatBigNumber(stats?.users || 0)} people</td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Played Shoorah's</td>
                  <td className="py-2 text-right">
                    {formatBigNumber(stats?.catalogueListeners || 0)} people
                  </td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Plays</td>
                  <td className="py-2 text-right">{formatBigNumber(stats?.cataloguePlays || 0)}</td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Listening time</td>
                  <td className="py-2 text-right">
                    {formatBigNumber(stats?.catalogueMinutes || 0)} min
                  </td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Saved from the catalogue</td>
                  <td className="py-2 text-right">{formatBigNumber(stats?.catalogueSaved || 0)}</td>
                </tr>
              </PanelTable>
            </Panel>
          )}
        </div>
      )}

      {tool === 'cleanse' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
            <StatCard
              icon={STAT_ICONS.completions}
              title="Records Completed"
              value={entries > 0 ? `${stats.completedRate}%` : '-'}
            />
            <StatCard
              icon={STAT_ICONS.goals}
              title="Action Steps Ticked"
              value={stats?.actionSteps > 0 ? `${stats.actionStepRate}%` : '-'}
            />
            <StatCard
              icon={STAT_ICONS.rating}
              title="Built With AI"
              value={entries > 0 ? `${stats.aiAssistedRate}%` : '-'}
            />
            <StatCard
              icon={STAT_ICONS.streak}
              title="With a Check-in Date"
              value={stats ? formatBigNumber(stats.withCheckin || 0) : '-'}
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
            {/* The point of the exercise is the reframe. Counting records
                started tells you nothing about whether anybody got there, so
                the four steps are shown as a funnel and the last one is the
                number that matters. */}
            <Panel
              title="Through the Thought Record"
              subtitle="How far people get — the reframe is the point"
            >
              {entries > 0 ? (
                shareTable(stats.stepsFunnel || [], 'Step')
              ) : (
                <PanelEmpty>{config.emptyMessage}</PanelEmpty>
              )}
            </Panel>

            <MiniDonutCard
              title="Record Status"
              subtitle="Draft, active or completed"
              data={stats?.statusSplit || []}
              colors={STATUS_RAMP}
              unit="records"
              totalLabel="Records"
              legendLimit={3}
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
            <Panel title="Action Steps" subtitle="Steps people set themselves, and tick off">
              {stats?.actionSteps > 0 ? (
                <PanelTable
                  head={
                    <>
                      <th className="py-2 pr-4 font-medium">Measure</th>
                      <th className="py-2 font-medium text-right">Count</th>
                    </>
                  }
                >
                  <tr className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">Steps set</td>
                    <td className="py-2 text-right">{formatBigNumber(stats.actionSteps)}</td>
                  </tr>
                  <tr className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">Steps ticked off</td>
                    <td className="py-2 text-right">{formatBigNumber(stats.actionStepsDone)}</td>
                  </tr>
                  <tr className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">Records with a reflection</td>
                    <td className="py-2 text-right">{formatBigNumber(stats.withReflection)}</td>
                  </tr>
                  {/* How many are categorised, never what the category says:
                      nothing server-side validates it, so the text is
                      untrusted and is not echoed. */}
                  <tr className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">Categorised</td>
                    <td className="py-2 text-right">
                      {formatBigNumber(stats.categorised)} ({stats.categorisedRate}%)
                    </td>
                  </tr>
                </PanelTable>
              ) : (
                <PanelEmpty>No action steps set yet.</PanelEmpty>
              )}
            </Panel>

            <MiniDonutCard
              title="Steps per Record"
              subtitle="Records that set any action steps"
              data={stats?.stepSizeSplit || []}
              colors={DURATION_RAMP}
              unit="records"
              totalLabel="Records"
              legendLimit={4}
            />
          </div>
        </>
      )}

      {tool === 'goals' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
            <StatCard
              icon={STAT_ICONS.completions}
              title="Goals Achieved"
              value={entries > 0 ? `${stats.achievedRate}%` : '-'}
            />
            {/* Actionable rather than a judgement: a goal past its date is a
                nudge waiting to happen. */}
            <StatCard
              icon={STAT_ICONS.more}
              title="Running Late"
              value={stats ? formatBigNumber(stats.overdue || 0) : '-'}
            />
            <StatCard
              icon={STAT_ICONS.goals}
              title="Checklist Ticked"
              value={stats?.checklistItems > 0 ? `${stats.checklistRate}%` : '-'}
            />
            <StatCard
              icon={STAT_ICONS.rating}
              title="Built With AI"
              value={entries > 0 ? `${stats.aiAssistedRate}%` : '-'}
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
            <MiniDonutCard
              title="Goal Status"
              subtitle="Draft, in progress or achieved"
              data={stats?.statusSplit || []}
              colors={STATUS_RAMP}
              unit="goals"
              totalLabel="Goals"
              legendLimit={3}
            />

            {/* A goal with a why, a how, a date and a checklist is a plan. One
                with a title and nothing else is a wish, and the two look
                identical in a goal count. */}
            <Panel title="Wish or Plan" subtitle="How much of the goal people actually fill in">
              {/* Overlapping again: a goal can have a why AND a date AND a
                  checklist. Each share is of all goals. */}
              {entries > 0 ? (
                shareTable(stats.depth || [], 'Filled in')
              ) : (
                <PanelEmpty>{config.emptyMessage}</PanelEmpty>
              )}
            </Panel>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
            <MiniDonutCard
              title="Time to Achieve"
              subtitle="Goals that were achieved, start to finish"
              data={stats?.timeToAchieveSplit || []}
              colors={DURATION_RAMP}
              unit="goals"
              totalLabel="Achieved"
              legendLimit={4}
            />
            <MiniDonutCard
              title="Steps per Goal"
              subtitle="Goals that have a checklist"
              data={stats?.checklistSizeSplit || []}
              colors={VOLUME_RAMP}
              unit="goals"
              totalLabel="Goals"
              legendLimit={4}
            />
          </div>
        </>
      )}
    </div>
  );
};

SelfDevelopmentToolPage.propTypes = {
  tool: PropTypes.oneOf(Object.keys(TOOL_CONFIG)).isRequired,
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string
};

export default SelfDevelopmentToolPage;
